import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Collapse,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AdaptiveImage from '../AdaptiveImage';
import MarkdownContent from '../markdown/MarkdownContent';
import RewriteParagraphDialog from '../dialogs/RewriteParagraphDialog';
import PartialRewriteDialog from '../dialogs/PartialRewriteDialog';
import ImageGeneratorDialog from '../ImageGeneratorDialog';
import { attachHeaderImage, writeParagraphStream } from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSnackbar } from 'notistack';
import { useCoins } from '../../contexts/CoinContext';

const ParagraphCard = ({
  paragraph,
  onEditParagraph,
  onWriteParagraph,
  onGenerateImage,
  onDeleteParagraph,
  onImageClick,
  loading,
  articleId,
  models,
  settings,
  selectedModel,
  setSelectedModel,
  onRewriteComplete,
  currentlyWriting = false,
  article
}) => {
  const { selectedProject } = useProject();
  const { showSubscriptionDialog } = useSubscription();
  const { enqueueSnackbar } = useSnackbar();
  const { spendCoins, updateCoins } = useCoins();
  const [rewriteDialogOpen, setRewriteDialogOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [headerImageContext, setHeaderImageContext] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const streamRef = useRef(null);
  const contentRef = useRef('');

  // Check if this is a social post type
  const isSocialPost = article?.type === 'short_social_post' || article?.type === 'long_social_post';

  const handleRewriteClick = (text) => {
    setSelectedText(text);
    setRewriteDialogOpen(true);
  };

  const handleRewriteComplete = (newContent) => {
    const updatedContent = paragraph.content.replace(selectedText, newContent);
    onRewriteComplete(updatedContent);
    setRewriteDialogOpen(false);
  };

  const handleHeaderImageClick = (headerText, sectionContent, level) => {
    setHeaderImageContext({
      headerText,
      sectionContent,
      level
    });
    setImageDialogOpen(true);
  };

  const handleImageGenerated = async (image) => {
    try {
      if (headerImageContext) {
        // If we're attaching to a header
        const headerPrefix = '#'.repeat(headerImageContext.level || 2); // Default to h2 if level not specified
        const fullHeaderText = `${headerPrefix} ${headerImageContext.headerText}`;
        await attachHeaderImage(articleId, paragraph.id, {
          image_id: image.id,
          header_text: fullHeaderText
        });
        onRewriteComplete(paragraph.content); // Refresh content
      }
      setImageDialogOpen(false);
      setHeaderImageContext(null);
    } catch (error) {
      console.error('Error attaching image:', error);
    }
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDeleteParagraph(paragraph);
  };

  const handleWriteClick = async () => {
    try {
      setIsStreaming(true);
      setStreamingContent('');
      contentRef.current = '';  // Reset content ref

      // Close any existing stream
      if (streamRef.current) {
        streamRef.current.close();
      }

      const { stream, close } = writeParagraphStream(articleId, paragraph.id, selectedModel);
      streamRef.current = { close };

      let messageCount = 0;

      const messageHandler = (event) => {
        try {
          const data = JSON.parse(event.data);
          messageCount++;
          
          console.log(`Message ${messageCount}:`, {
            is_complete: data.is_complete,
            content_length: data.content?.length || 0,
            credits_used: data.credits_used,
            data: data
          });
          
          if (data.error) {
            console.error('Stream error:', data.error);
            enqueueSnackbar(data.error, { variant: 'error' });
            setIsStreaming(false);
            stream.close();
            return;
          }

          if (!data.is_complete) {
            // Log the incoming token
            console.log(`Message ${messageCount}:`, {
              newToken: data.content,
              contentLength: contentRef.current.length
            });

            // Update the content ref and state
            contentRef.current += data.content;
            setStreamingContent(contentRef.current);

            // Log after update
            console.log(`After update:`, {
              contentLength: contentRef.current.length,
              lastToken: data.content
            });
          } else {
            // Final message
            console.log('Final message:', {
              totalMessages: messageCount,
              finalContentLength: contentRef.current.length,
              isStreaming: isStreaming,
              currentlyWriting: currentlyWriting
            });

            // Update the paragraph content
            if (data.credits_used) {
              spendCoins(data.credits_used);
              updateCoins(); // Update coins display in TopBar
              enqueueSnackbar(`-${data.credits_used.toFixed(2)} coins used`, { 
                variant: 'info',
                autoHideDuration: 3000
              });
            }
            onRewriteComplete(contentRef.current);
            setStreamingContent(''); // Clear streaming content
            setIsStreaming(false); // Reset streaming state
            stream.close();
          }
        } catch (error) {
          console.error('Error parsing stream data:', error);
          enqueueSnackbar('Error processing response', { variant: 'error' });
          setIsStreaming(false);
          stream.close();
        }
      };

      const errorHandler = (error) => {
        console.error('Stream error:', error);
        enqueueSnackbar('Error streaming content', { variant: 'error' });
        setIsStreaming(false);
        stream.close();
      };

      // Add event listeners
      stream.addEventListener('message', messageHandler);
      stream.addEventListener('error', errorHandler);

      // Store cleanup function in streamRef
      streamRef.current = {
        close: () => {
          stream.removeEventListener('message', messageHandler);
          stream.removeEventListener('error', errorHandler);
          stream.close();
        }
      };

    } catch (error) {
      console.error('Error setting up stream:', error);
      enqueueSnackbar('Error setting up content stream', { variant: 'error' });
      setIsStreaming(false);
    }
  };

  // Clean up stream on unmount
  React.useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.close();
      }
    };
  }, []);

  return (
    <Card 
      id={`paragraph-${paragraph.id}`}
      sx={{ 
        position: 'relative',
        ...(currentlyWriting && {
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: 3
        })
      }}
    >
      {(currentlyWriting || isStreaming) && (
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1
        }}>
          <LinearProgress />
        </Box>
      )}
      <CardContent>
        {/* Header section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'flex-start' }, 
          gap: 2,
          mb: 2 
        }}>
          <Box
            sx={{
              position: 'relative',
              '&:hover .ai-button': { opacity: 1 },
              paddingRight: '48px',
              flex: 1,
            }}
          >
            <Typography variant="h6">{paragraph.heading}</Typography>
            <IconButton
              className="ai-button"
              size="small"
              onClick={() => handleRewriteClick(paragraph.heading)}
              sx={{
                position: 'absolute',
                right: '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: { xs: 1, sm: 0 },
                transition: 'opacity 0.2s',
                backgroundColor: 'background.paper',
                boxShadow: 1,
                width: '36px',
                height: '36px',
                color: 'primary.main',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                },
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'common.white',
                },
              }}
            >
              <AutoFixHighIcon />
            </IconButton>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexDirection: { xs: 'row', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
          }}>
            {/* Mobile View */}
            <Box sx={{ 
              display: { xs: 'flex', sm: 'none' }, 
              gap: 1,
              flex: 1,
              order: 1
            }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<EditNoteIcon />}
                onClick={() => onEditParagraph(paragraph)}
              >
                Edit
              </Button>
              {paragraph.content ? (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={() => handleRewriteClick(paragraph.content)}
                  disabled={loading}
                >
                  Rewrite
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={handleWriteClick}
                  disabled={loading}
                >
                  Write
                </Button>
              )}
            </Box>
            <Box sx={{ 
              display: { xs: 'flex', sm: 'none' }, 
              gap: 1,
              alignItems: 'center',
              order: 2
            }}>
              <IconButton
                size="small"
                onClick={() => onGenerateImage(paragraph)}
                sx={{ color: 'primary.main' }}
              >
                <ImageIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{ color: 'text.secondary' }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>

            {/* Desktop View */}
            <Box sx={{ 
              display: { xs: 'none', sm: 'flex' }, 
              gap: 1
            }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditNoteIcon />}
                onClick={() => onEditParagraph(paragraph)}
              >
                Edit
              </Button>
              {paragraph.content ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={() => handleRewriteClick(paragraph.content)}
                  disabled={loading}
                >
                  Rewrite All
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={handleWriteClick}
                  disabled={loading}
                >
                  Write
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<ImageIcon />}
                onClick={() => onGenerateImage(paragraph)}
              >
                Generate Image
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onDeleteParagraph(paragraph)}
                disabled={loading}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Mobile Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Paragraph</ListItemText>
          </MenuItem>
        </Menu>

        {/* Desktop layout container */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 3 }
        }}>
          {/* Image section */}
          {(paragraph.image_url || isSocialPost) && (
            <Box sx={{ 
              width: { xs: '100%', sm: '20%' },
              flexShrink: 0
            }}>
              <Card variant="outlined">
                <Box 
                  sx={{ 
                    position: 'relative',
                    height: { xs: 200, sm: '100%' },
                    minHeight: { sm: 200 },
                    '&:hover': {
                      cursor: 'pointer',
                      '& .zoom-icon': {
                        opacity: 1
                      }
                    }
                  }}
                  onClick={() => paragraph.image_url ? 
                    onImageClick(paragraph.image_url, `Image for ${paragraph.heading}`) : 
                    onGenerateImage(paragraph)
                  }
                >
                  {paragraph.image_url ? (
                    <>
                      <AdaptiveImage
                        imageUrl={paragraph.image_url}
                        alt={`Image for ${paragraph.heading}`}
                        sx={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <Box
                        className="zoom-icon"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.4)',
                          opacity: { xs: 1, sm: 0 },
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <ZoomOutMapIcon sx={{ color: 'white' }} />
                      </Box>
                      <Box sx={{ 
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        opacity: { xs: 1, sm: 0 },
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          opacity: 1
                        }
                      }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onGenerateImage(paragraph);
                          }}
                          sx={{
                            bgcolor: 'background.paper',
                            '&:hover': {
                              bgcolor: 'background.paper'
                            }
                          }}
                        >
                          <AutoFixHighIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Click to add image
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Box>
          )}

          {/* Content section */}
          <Box sx={{ 
            flex: 1,
            width: { xs: '100%', sm: '80%' }
          }}>
            {isStreaming ? (
              <>
                <LinearProgress sx={{ mb: 2 }} />
                <Collapse in={true}>
                  <Box sx={{ 
                    position: 'relative',
                    minHeight: '100px',
                    '& .fade-overlay': {
                      opacity: 0.7
                    }
                  }}>
                    <MarkdownContent 
                      content={streamingContent} 
                      onRewriteClick={handleRewriteClick}
                      onHeaderImageClick={handleHeaderImageClick}
                    />
                    <Box 
                      className="fade-overlay"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '20px',
                        background: 'linear-gradient(transparent, white)'
                      }}
                    />
                  </Box>
                </Collapse>
              </>
            ) : paragraph.content ? (
              <MarkdownContent 
                content={paragraph.content} 
                onRewriteClick={handleRewriteClick}
                onHeaderImageClick={handleHeaderImageClick}
              />
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                py: 2
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Content not generated yet
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={handleWriteClick}
                  disabled={loading}
                >
                  Write this section
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <PartialRewriteDialog
          open={rewriteDialogOpen}
          onClose={() => setRewriteDialogOpen(false)}
          paragraph={paragraph}
          articleId={articleId}
          selectedText={selectedText}
          onSave={handleRewriteComplete}
          loading={loading}
          models={models}
          settings={settings}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
        />

        <ImageGeneratorDialog
          open={imageDialogOpen}
          onClose={() => {
            setImageDialogOpen(false);
            setHeaderImageContext(null);
          }}
          articleId={articleId}
          paragraphId={paragraph.id}
          paragraphContent={headerImageContext?.sectionContent || paragraph.content}
          paragraphHeading={headerImageContext?.headerText || paragraph.heading}
          onImageGenerated={handleImageGenerated}
          contentOnly={Boolean(headerImageContext)}
          projectId={selectedProject?.id}
        />
      </CardContent>
    </Card>
  );
};

export default ParagraphCard; 